// src/views/analytics/executiveAnalytics.js
// ─────────────────────────────────────────────────────────────────────────────
// FlowSense MOW+ — pure analytics helpers for the Executive Overview (round 4).
// No React, no I/O: every function takes real data and returns numbers or null. Nothing here
// generates values — a statistic that needs more data than it has returns null and the UI shows
// "—". Exported individually so they can be unit tested.
// ─────────────────────────────────────────────────────────────────────────────

export const isNum = (v) => v !== null && v !== undefined && v !== '' && Number.isFinite(Number(v));
export const num = (v) => (isNum(v) ? Number(v) : null);

export const mean = (arr) => {
  const xs = (arr || []).filter(isNum).map(Number);
  return xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null;
};

/** Population standard deviation (null for fewer than 2 values). */
export const stdDev = (arr) => {
  const xs = (arr || []).filter(isNum).map(Number);
  if (xs.length < 2) return null;
  const m = xs.reduce((a, b) => a + b, 0) / xs.length;
  return Math.sqrt(xs.reduce((a, b) => a + (b - m) ** 2, 0) / xs.length);
};

// ── Formatting (null → "—") ─────────────────────────────────────────────────
const DASH = '—';
export const fmtNum = (v, dp = 0) => (isNum(v) ? Number(v).toLocaleString(undefined, { maximumFractionDigits: dp, minimumFractionDigits: 0 }) : DASH);
export const fmtPct = (v, dp = 1) => (isNum(v) ? `${Number(v).toFixed(dp)}%` : DASH);
export const fmtMoney = (v, { compact = false } = {}) => {
  if (!isNum(v)) return DASH;
  const n = Number(v);
  if (compact && Math.abs(n) >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (compact && Math.abs(n) >= 1e4) return `$${(n / 1e3).toFixed(0)}k`;
  return `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};
export const fmtSigned = (v, dp = 1, unit = '%') => (isNum(v) ? `${Number(v) > 0 ? '+' : ''}${Number(v).toFixed(dp)}${unit}` : DASH);

// ── Dates ───────────────────────────────────────────────────────────────────
export const toYmd = (d) => {
  if (!d) return null;
  if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}/.test(d)) return d.slice(0, 10);
  const x = d instanceof Date ? d : new Date(d);
  return Number.isNaN(x.getTime()) ? null : x.toISOString().slice(0, 10);
};
const DAY = 86400000;
const parseYmd = (s) => { const [y, m, d] = s.split('-').map(Number); return Date.UTC(y, m - 1, d); };
export const daysBetween = (a, b) => Math.round((parseYmd(b) - parseYmd(a)) / DAY);
export const addDays = (ymd, n) => new Date(parseYmd(ymd) + n * DAY).toISOString().slice(0, 10);

/**
 * Normalises the AnalyticsPage date range ({startDate,endDate} | {start,end}, Date or string) and
 * derives the previous period of the same length. Defaults to the last 30 days.
 */
export function resolvePeriod(dateRange, today = toYmd(new Date())) {
  const end = toYmd(dateRange?.endDate ?? dateRange?.end) || today;
  const start = toYmd(dateRange?.startDate ?? dateRange?.start) || addDays(end, -29);
  const span = Math.max(daysBetween(start, end) + 1, 1);
  const prevEnd = addDays(start, -1);
  const prevStart = addDays(prevEnd, -(span - 1));
  // kpi-history always ends today; ask for enough days to cover the previous period too.
  const historyDays = Math.min(Math.max(daysBetween(prevStart, today) + 1, 7), 366);
  return { start, end, span, prevStart, prevEnd, historyDays };
}

export const inRange = (date, a, b) => Boolean(date) && date >= a && date <= b;

// ── KPI history → sparklines / previous-period values ───────────────────────
// How each KPI's daily series rolls up into one number for a period.
export const KPI_AGG = { openWorkOrders: 'last', slaCompliance: 'mean', totalMaintenanceCost: 'sum', criticalAlerts: 'sum', systemUptime: 'mean' };

export function aggregateSeries(points, how) {
  const vals = (points || []).map(p => p.value).filter(isNum).map(Number);
  if (!vals.length) return null;
  if (how === 'sum') return vals.reduce((a, b) => a + b, 0);
  if (how === 'last') return vals[vals.length - 1];
  return vals.reduce((a, b) => a + b, 0) / vals.length;
}

/** Splits kpi-history series into the current/previous period with aggregates. */
export function periodSeries(history, period) {
  const out = {};
  const series = history?.series || {};
  Object.keys(KPI_AGG).forEach((key) => {
    const s = Array.isArray(series[key]) ? series[key] : null;
    if (!s) { out[key] = { available: false, current: [], previous: [], currentAgg: null, previousAgg: null }; return; }
    const current = s.filter(p => inRange(p.date, period.start, period.end));
    const previous = s.filter(p => inRange(p.date, period.prevStart, period.prevEnd));
    out[key] = {
      available: true,
      current: current.map(p => ({ date: p.date, value: num(p.value) })),
      previous: previous.map(p => ({ date: p.date, value: num(p.value) })),
      currentAgg: aggregateSeries(current, KPI_AGG[key]),
      previousAgg: aggregateSeries(previous, KPI_AGG[key]),
    };
  });
  return out;
}

export function seriesStats(points) {
  const vals = (points || []).map(p => p.value).filter(isNum).map(Number);
  if (!vals.length) return { n: 0, mean: null, sd: null, min: null, max: null, cv: null };
  const m = mean(vals); const sd = stdDev(vals);
  return { n: vals.length, mean: m, sd, min: Math.min(...vals), max: Math.max(...vals), cv: sd !== null && m ? (sd / Math.abs(m)) * 100 : null };
}

// ── Regression / smoothing ──────────────────────────────────────────────────
export function linearRegression(values) {
  const ys = (values || []).map(num);
  const pts = ys.map((y, x) => [x, y]).filter(([, y]) => y !== null);
  const n = pts.length;
  if (n < 2) return null;
  const xbar = pts.reduce((a, [x]) => a + x, 0) / n;
  const ybar = pts.reduce((a, [, y]) => a + y, 0) / n;
  const sxx = pts.reduce((a, [x]) => a + (x - xbar) ** 2, 0);
  const sxy = pts.reduce((a, [x, y]) => a + (x - xbar) * (y - ybar), 0);
  const slope = sxx === 0 ? 0 : sxy / sxx;
  const intercept = ybar - slope * xbar;
  const ssRes = pts.reduce((a, [x, y]) => a + (y - (slope * x + intercept)) ** 2, 0);
  const ssTot = pts.reduce((a, [, y]) => a + (y - ybar) ** 2, 0);
  const r2 = ssTot === 0 ? null : Math.max(0, 1 - ssRes / ssTot);
  const se = n > 2 ? Math.sqrt(ssRes / (n - 2)) : null;
  return { slope, intercept, r2, se, n, xbar, sxx };
}

export const sma = (values, period = 3) => values.map((_, i) => {
  if (i < period - 1) return null;
  const w = values.slice(i - period + 1, i + 1);
  return w.some(v => !isNum(v)) ? null : w.reduce((a, b) => a + Number(b), 0) / period;
});

export const ema = (values, period = 3) => {
  const k = 2 / (period + 1);
  let prev = null;
  return values.map((v) => {
    if (!isNum(v)) return prev;
    prev = prev === null ? Number(v) : Number(v) * k + prev * (1 - k);
    return prev;
  });
};

export const bollinger = (values, period = 5, mult = 2) => values.map((_, i) => {
  if (i < period - 1) return { upper: null, lower: null, middle: null };
  const w = values.slice(i - period + 1, i + 1).filter(isNum).map(Number);
  if (w.length < period) return { upper: null, lower: null, middle: null };
  const m = w.reduce((a, b) => a + b, 0) / w.length;
  const sd = Math.sqrt(w.reduce((a, b) => a + (b - m) ** 2, 0) / w.length);
  return { upper: m + mult * sd, lower: Math.max(0, m - mult * sd), middle: m };
});

// Two-sided 95 % Student-t critical values by degrees of freedom (normal beyond 30).
const T95 = [null, 12.706, 4.303, 3.182, 2.776, 2.571, 2.447, 2.365, 2.306, 2.262, 2.228, 2.201, 2.179, 2.16, 2.145, 2.131, 2.12, 2.11, 2.101, 2.093, 2.086, 2.08, 2.074, 2.069, 2.064, 2.06, 2.056, 2.052, 2.048, 2.045, 2.042];
export const t95 = (df) => (df >= 1 && df <= 30 ? T95[df] : 1.96);

const nextMonth = (ym, k) => {
  const [y, m] = String(ym).split('-').map(Number);
  if (!y || !m) return `+${k}`;
  const d = new Date(Date.UTC(y, m - 1 + k, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
};

/**
 * Monthly cost rows ({month:'YYYY-MM', totalCost}) → enriched chart rows + anomalies + forecast.
 *   SMA(3) / EMA(3) / Bollinger(5, 2σ) only where there is enough history;
 *   anomalies: |z| > 2 against the other months (needs ≥ 4 months);
 *   forecast: linear trend with a 95 % prediction interval (needs ≥ 4 months). The interval —
 *   not an invented "confidence" — says how much to trust it; R² is reported alongside.
 */
export function analyseCost(rows, { horizon = 3 } = {}) {
  const clean = (rows || [])
    .map(r => ({ ...r, month: r.month || r.period || r.date || null, totalCost: num(r.totalCost ?? r.total_cost ?? r.cost) }))
    .filter(r => r.month)
    .sort((a, b) => String(a.month).localeCompare(String(b.month)));
  const values = clean.map(r => r.totalCost);
  const s = sma(values, 3); const e = ema(values, 3); const b = bollinger(values, 5, 2);

  const anomalies = [];
  if (values.filter(isNum).length >= 4) {
    values.forEach((v, i) => {
      if (!isNum(v)) return;
      const others = values.filter((x, j) => j !== i && isNum(x)).map(Number);
      const m = mean(others); const sd = stdDev(others);
      if (!sd) return;
      const z = (v - m) / sd;
      if (Math.abs(z) > 2) {
        anomalies.push({
          month: clean[i].month, totalCost: v, zScore: z, change: m ? ((v - m) / Math.abs(m)) * 100 : null,
          type: z > 0 ? 'increase' : 'decrease', severity: Math.abs(z) > 3 ? 'critical' : Math.abs(z) > 2.5 ? 'high' : 'medium',
          baselineMean: m,
        });
      }
    });
  }
  const anomalyMonths = new Set(anomalies.map(a => a.month));

  const chart = clean.map((r, i) => ({
    ...r, sma: s[i], ema: e[i], upperBand: b[i].upper, lowerBand: b[i].lower,
    anomaly: anomalyMonths.has(r.month) ? r.totalCost : null,
  }));

  let forecast = null;
  const reg = linearRegression(values);
  if (reg && reg.n >= 4 && reg.se !== null) {
    const lastX = values.length - 1;
    const t = t95(reg.n - 2);
    const lastMonth = clean[clean.length - 1].month;
    forecast = {
      r2: reg.r2, slope: reg.slope, n: reg.n, method: 'linear trend, 95 % prediction interval',
      points: Array.from({ length: horizon }, (_, k) => {
        const x = lastX + k + 1;
        const yhat = reg.slope * x + reg.intercept;
        const half = t * reg.se * Math.sqrt(1 + 1 / reg.n + (reg.sxx ? ((x - reg.xbar) ** 2) / reg.sxx : 0));
        return { month: nextMonth(lastMonth, k + 1), predicted: Math.max(0, yhat), lower: Math.max(0, yhat - half), upper: Math.max(0, yhat + half) };
      }),
    };
  }

  const vals = values.filter(isNum).map(Number);
  const trend = reg ? {
    slope: reg.slope, intercept: reg.intercept, r2: reg.r2, n: reg.n,
    mean: mean(vals), sd: stdDev(vals), min: vals.length ? Math.min(...vals) : null, max: vals.length ? Math.max(...vals) : null,
    totalChange: vals.length >= 2 && vals[0] ? ((vals[vals.length - 1] - vals[0]) / Math.abs(vals[0])) * 100 : null,
    volatility: (() => { const m = mean(vals); const sd = stdDev(vals); return m && sd !== null ? (sd / Math.abs(m)) * 100 : null; })(),
    direction: (() => {
      const sd = stdDev(vals);
      if (sd === null || Math.abs(reg.slope) <= sd * 0.25) return 'stable';
      return reg.slope > 0 ? 'upward' : 'downward';
    })(),
  } : null;

  return { chart, anomalies, forecast, trend };
}

// ── Hotspots: grid density instead of a constant intensity ──────────────────
const SEV_WEIGHT = { critical: 1, high: 0.75, medium: 0.5, low: 0.25 };

/**
 * Groups issue points into ~cellDeg grid cells. Intensity = severity-weighted count in the cell
 * divided by the densest cell (0..1). Points without coordinates are counted as `unlocated`.
 */
export function buildHotspots(points, { cellDeg = 0.01 } = {}) {
  const cells = new Map();
  let located = 0; let unlocated = 0;
  (points || []).forEach((p) => {
    const lat = num(p.latitude ?? p.lat); const lng = num(p.longitude ?? p.lng);
    if (lat === null || lng === null || Math.abs(lat) > 90 || Math.abs(lng) > 180 || (lat === 0 && lng === 0)) { unlocated += 1; return; }
    located += 1;
    const key = `${Math.floor(lat / cellDeg)}:${Math.floor(lng / cellDeg)}`;
    const c = cells.get(key) || { latSum: 0, lngSum: 0, count: 0, weight: 0, severities: {}, types: {} };
    c.latSum += lat; c.lngSum += lng; c.count += 1;
    const sev = String(p.severity || '').toLowerCase();
    c.weight += SEV_WEIGHT[sev] ?? 0.5;
    c.severities[sev || 'unknown'] = (c.severities[sev || 'unknown'] || 0) + 1;
    const type = p.issue_type || p.type || 'Unspecified';
    c.types[type] = (c.types[type] || 0) + 1;
    cells.set(key, c);
  });
  const maxW = Math.max(0, ...[...cells.values()].map(c => c.weight));
  const hotspots = [...cells.values()].map(c => ({
    position: [c.latSum / c.count, c.lngSum / c.count],
    count: c.count,
    intensity: maxW ? c.weight / maxW : 0,
    severities: c.severities,
    types: c.types,
  })).sort((a, b) => b.intensity - a.intensity || b.count - a.count);
  return { hotspots, located, unlocated, cellDeg };
}

// ── Health scorecard from real KPIs ─────────────────────────────────────────
/**
 * Each measure scores 0–100 only when its input exists; the overall score is the weighted mean of
 * the available measures (weights re-normalised) and says how many measures it used.
 */
export function healthScore(kpis, targets = {}) {
  const k = kpis || {};
  const measures = [];
  const uptime = num(k.systemUptime); const sla = num(k.slaCompliance); const alerts = num(k.criticalAlerts);
  const cost = num(k.totalMaintenanceCost); const backlogTrend = num(k.openWorkOrders_trend);
  const tUp = num(targets.uptime) || 99; const tSla = num(targets.sla) || 95; const budget = num(targets.budget);
  if (uptime !== null) measures.push({ key: 'uptime', label: 'Uptime', weight: 0.3, value: Math.min(100, (uptime / tUp) * 100), basis: `${fmtPct(uptime)} vs target ${tUp}%` });
  if (sla !== null) measures.push({ key: 'sla', label: 'SLA', weight: 0.25, value: Math.min(100, (sla / tSla) * 100), basis: `${fmtPct(sla)} vs target ${tSla}%` });
  if (alerts !== null) measures.push({ key: 'alerts', label: 'Alerts', weight: 0.2, value: Math.max(0, 100 - alerts * 10), basis: `${alerts} active critical (−10 each)` });
  if (cost !== null && budget) measures.push({ key: 'cost', label: 'Budget', weight: 0.15, value: cost <= budget ? 100 : Math.max(0, 100 - ((cost - budget) / budget) * 100), basis: `${fmtMoney(cost)} vs budget ${fmtMoney(budget)}` });
  if (backlogTrend !== null) measures.push({ key: 'backlog', label: 'Backlog', weight: 0.1, value: backlogTrend <= 0 ? 100 : Math.max(0, 100 - backlogTrend * 2), basis: `open work orders ${fmtSigned(backlogTrend)} vs previous period` });
  const w = measures.reduce((a, m) => a + m.weight, 0);
  const score = w ? Math.round(measures.reduce((a, m) => a + m.value * m.weight, 0) / w) : null;
  const label = score === null ? 'No data' : score >= 90 ? 'Excellent' : score >= 75 ? 'Good' : score >= 60 ? 'Fair' : 'Needs attention';
  return { score, label, measures, used: measures.length, possible: 5 };
}

// ── KPI alerts (stable keys so the UI only notifies on change) ──────────────
export function kpiAlerts(kpis, targets = {}) {
  const k = kpis || {};
  const out = [];
  const uptime = num(k.systemUptime); const sla = num(k.slaCompliance); const alerts = num(k.criticalAlerts);
  const cost = num(k.totalMaintenanceCost); const budget = num(targets.budget);
  if (uptime !== null && uptime < 95) out.push({ key: 'uptime-low', kpi: 'systemUptime', status: 'warning', title: 'Low asset availability', message: `${fmtPct(uptime)} of assets are operational.` });
  if (sla !== null && sla < 90) out.push({ key: 'sla-low', kpi: 'slaCompliance', status: 'error', title: 'SLA below 90 %', message: `${fmtPct(sla)} of work orders met their SLA.` });
  if (alerts !== null && alerts > 5) out.push({ key: 'alerts-high', kpi: 'criticalAlerts', status: 'error', title: 'Many critical alerts', message: `${alerts} critical sensor alerts are active.` });
  if (cost !== null && budget && cost > budget) out.push({ key: 'budget-over', kpi: 'totalMaintenanceCost', status: 'warning', title: 'Over budget', message: `${fmtMoney(cost)} spent against a ${fmtMoney(budget)} budget.` });
  return out;
}

// ── Live events → activity entries ──────────────────────────────────────────
const LIVE_TEXT = {
  new_sensor_alert: (p) => ({ severity: String(p?.severity).toLowerCase() === 'critical' ? 'critical' : 'warning', title: p?.alert_message || p?.message || `New sensor alert${p?.sensor_id ? ` on ${p.sensor_id}` : ''}` }),
  critical_anomaly: (p) => ({ severity: 'critical', title: p?.message || `Critical anomaly${p?.sensorId || p?.sensor_id ? ` on ${p.sensorId || p.sensor_id}` : ''}` }),
  asset_prediction_update: (p) => ({ severity: p?.health_status === 'critical' ? 'critical' : 'info', title: `RUL updated for ${p?.assetName || p?.asset_name || p?.assetId || p?.asset_id || 'an asset'}${isNum(p?.predicted_rul_days) ? ` — ${p.predicted_rul_days} days` : ''}` }),
  predictive_risk_update: (p) => ({ severity: 'warning', title: p?.message || 'Predictive risk changed' }),
  predictive_analysis_complete: (p) => ({ severity: 'success', title: `Predictive analysis complete${isNum(p?.count ?? p?.assetsAnalyzed) ? ` — ${p.count ?? p.assetsAnalyzed} assets scored` : ''}` }),
  rul_batch_complete: (p) => ({ severity: 'success', title: `RUL batch complete${isNum(p?.count) ? ` (${p.count})` : ''}` }),
  ai_flood_prediction: (p) => ({ severity: ['CRITICAL', 'HIGH'].includes(p?.predictedSeverity || p?.flood_risk) ? 'critical' : 'info', title: `${p?.wellName || 'Well'}: flood risk ${isNum(p?.flood_probability) ? `${Math.round(p.flood_probability * 100)}%` : 'updated'}${p?.source === 'rule_fallback' ? ' (rule-based)' : ''}` }),
  predictive_alerts_updated: () => ({ severity: 'info', title: 'Predictive alerts updated' }),
  asset_health_scores_updated: () => ({ severity: 'info', title: 'Asset health scores recalculated' }),
  ml_events: (p) => ({ severity: p?.type === 'job_finished' && p?.status === 'failed' ? 'warning' : 'info', title: p?.message || `Model event: ${String(p?.type || 'update').replace(/_/g, ' ')}${p?.modelType || p?.model_type ? ` (${p.modelType || p.model_type})` : ''}` }),
  ai_model_promoted: (p) => ({ severity: 'success', title: `Model promoted${p?.modelType ? `: ${p.modelType}` : ''}${p?.version ? ` v${p.version}` : ''}` }),
};

export function liveEventToActivity(ev) {
  if (!ev || !ev.type) return null;
  const at = ev.at ? new Date(ev.at).toISOString() : new Date().toISOString();
  if (ev.type === 'ai_activity' && ev.payload && ev.payload.title) {
    const p = ev.payload;
    return { id: p.id ? `act-${p.id}` : `live-${ev.type}-${at}`, severity: p.severity || 'info', title: p.title, detail: p.detail || null, link: p.link || null, at: p.at || at, source: 'live' };
  }
  const f = LIVE_TEXT[ev.type];
  if (!f) return null;
  const { severity, title } = f(ev.payload || {});
  return { id: `live-${ev.type}-${at}`, severity, title, detail: null, link: null, at, source: 'live' };
}

export function mergeActivity(prev, incoming, cap = 40) {
  const seen = new Set();
  return [...(incoming || []), ...(prev || [])]
    .filter(Boolean)
    .filter(e => (seen.has(e.id) ? false : (seen.add(e.id), true)))
    .sort((a, b) => String(b.at).localeCompare(String(a.at)))
    .slice(0, cap);
}

// ── Tiny markdown → blocks (executive briefing) ─────────────────────────────
export function markdownBlocks(text) {
  return String(text || '').split(/\r?\n/).reduce((acc, raw) => {
    const line = raw.trimEnd();
    if (!line.trim()) return acc;
    const h = line.match(/^(#{1,3})\s+(.*)$/);
    if (h) acc.push({ kind: `h${h[1].length}`, text: h[2] });
    else if (/^\s*[-*•]\s+/.test(line)) acc.push({ kind: 'li', text: line.replace(/^\s*[-*•]\s+/, '') });
    else if (/^\s*\d+[.)]\s+/.test(line)) acc.push({ kind: 'li', text: line.replace(/^\s*\d+[.)]\s+/, '') });
    else acc.push({ kind: 'p', text: line });
    return acc;
  }, []);
}

/** Executive briefing markdown from the ML Ops briefing (used when /reports is unavailable). */
export function briefingToMarkdown(b) {
  if (!b) return '';
  const lines = [`# Executive briefing — ${b.range?.start || ''} to ${b.range?.end || ''}`];
  if (b.headline) lines.push('', b.headline);
  if (isNum(b.score)) lines.push('', `**Health score:** ${b.score}/100 (${b.scoreLabel || ''})`);
  if (b.narrative?.text) lines.push('', '## Summary', b.narrative.text);
  const kpis = (b.kpis || []).filter(k => k.available);
  if (kpis.length) {
    lines.push('', '## Key indicators');
    kpis.forEach((k) => {
      const unit = k.unit === '%' ? '%' : '';
      const cur = isNum(k.current) ? `${Number(k.current).toLocaleString()}${unit}` : '—';
      const prev = isNum(k.previous) ? `${Number(k.previous).toLocaleString()}${unit}` : '—';
      lines.push(`- **${k.label}:** ${cur} (previous ${prev}${k.change?.assessment ? `, ${k.change.assessment}` : ''})`);
    });
  }
  const ins = (b.insights || []).slice(0, 8);
  if (ins.length) {
    lines.push('', '## Findings');
    ins.forEach(i => lines.push(`- **${i.title}** — ${i.detail || ''}`.trim()));
  }
  if (b.ml) lines.push('', '## AI / ML', `- ${b.ml.inProduction} of ${b.ml.trainable} models in production; engine ${b.ml.mlOnline ? 'online' : 'offline'}; ${b.ml.activeJobs} training run(s) active.`);
  return lines.join('\n');
}
